import { supabase } from '../lib/supabase';

export const transactionService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('transactions')?.select(`
          id,
          user_id,
          account_id,
          category_id,
          date,
          description,
          amount,
          type,
          cleared,
          notes,
          recurring_transaction_id,
          created_at,
          updated_at,
          accounts!inner(id, name, account_group),
          category:categories(id, name, color, icon)
        `)?.order('date', { ascending: false });

      if (filters?.accountId) {
        query = query?.eq('account_id', filters?.accountId);
      }
      if (filters?.categoryId) {
        query = query?.eq('category_id', filters?.categoryId);
      }
      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }
      if (filters?.dateFrom) {
        query = query?.gte('date', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('date', filters?.dateTo);
      }
      if (filters?.cleared !== undefined) {
        query = query?.eq('cleared', filters?.cleared);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        accountId: row?.account_id,
        account: row?.accounts?.name || '',
        accountGroup: row?.accounts?.account_group || '',
        categoryId: row?.category_id,
        category: row?.category?.name || '',
        categoryColor: row?.category?.color || '#6366f1',
        categoryIcon: row?.category?.icon || 'Tag',
        date: row?.date,
        description: row?.description,
        amount: parseFloat(row?.amount),
        type: row?.type,
        cleared: row?.cleared,
        notes: row?.notes,
        recurringTransactionId: row?.recurring_transaction_id,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  },

  async getByAccount(accountId) {
    try {
      const { data, error } = await supabase?.from('transactions')?.select(`
          id,
          user_id,
          account_id,
          category_id,
          date,
          description,
          amount,
          type,
          cleared,
          notes,
          recurring_transaction_id,
          created_at,
          updated_at,
          accounts!inner(id, name, account_group),
          category:categories(id, name, color, icon)
        `)?.eq('account_id', accountId)?.order('date', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        accountId: row?.account_id,
        accountName: row?.accounts?.name || '',
        accountGroup: row?.accounts?.account_group || '',
        categoryId: row?.category_id,
        category: row?.category?.name || '',
        categoryColor: row?.category?.color || '#6366f1',
        categoryIcon: row?.category?.icon || 'Tag',
        date: row?.date,
        description: row?.description,
        amount: parseFloat(row?.amount),
        type: row?.type,
        cleared: row?.cleared,
        notes: row?.notes,
        recurringTransactionId: row?.recurring_transaction_id,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching transactions by account:', error);
      throw error;
    }
  },

  async create(transactionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // CRITICAL: Validate type before sending to database
      const validTypes = ['income', 'expense', 'transfer'];
      const normalizedType = transactionData?.type?.toLowerCase()?.trim();
      
      if (!validTypes?.includes(normalizedType)) {
        console.error('[transactionService.create] Invalid transaction type:', transactionData?.type);
        throw new Error(`Invalid transaction type: ${transactionData?.type}. Must be one of: ${validTypes.join(', ')}`);
      }
      
      console.log('[transactionService.create] Creating transaction with type:', normalizedType);
      console.log('[transactionService.create] Full transaction data:', JSON.stringify(transactionData, null, 2));

      const { data, error } = await supabase?.from('transactions')?.insert({
          user_id: user?.id,
          account_id: transactionData?.accountId,
          category_id: transactionData?.categoryId || null,
          date: transactionData?.date,
          description: transactionData?.description,
          amount: transactionData?.amount,
          type: normalizedType,
          cleared: transactionData?.cleared || false,
          notes: transactionData?.notes || null,
          recurring_transaction_id: transactionData?.recurringTransactionId || null
        })?.select(`
          id,
          user_id,
          account_id,
          category_id,
          date,
          description,
          amount,
          type,
          cleared,
          notes,
          recurring_transaction_id,
          created_at,
          updated_at,
          accounts!inner(id, name, account_group),
          category:categories(id, name, color, icon)
        `)?.single();
      
      if (error) throw error;
      
      console.log('[transactionService.create] Transaction created successfully');
      console.log('[transactionService.create] Returned type from DB:', data?.type);
      
      // VALIDATION: Check if database returned different type than what we sent
      if (data?.type !== normalizedType) {
        console.error('[transactionService.create] DATABASE TYPE MISMATCH!');
        console.error('[transactionService.create]   - Sent to DB:', normalizedType);
        console.error('[transactionService.create]   - Received from DB:', data?.type);
        console.error('[transactionService.create]   - This indicates a database trigger is modifying the type!');
      }
      
      return {
        id: data?.id,
        userId: data?.user_id,
        accountId: data?.account_id,
        account: data?.accounts?.name || '',
        accountGroup: data?.accounts?.account_group || '',
        categoryId: data?.category_id,
        category: data?.category?.name || '',
        categoryColor: data?.category?.color || '#6366f1',
        categoryIcon: data?.category?.icon || 'Tag',
        date: data?.date,
        description: data?.description,
        amount: parseFloat(data?.amount),
        type: data?.type,
        cleared: data?.cleared,
        notes: data?.notes,
        recurringTransactionId: data?.recurring_transaction_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  },

  async update(transactionId, updates) {
    try {
      const updateData = {};
      if (updates?.accountId !== undefined) updateData.account_id = updates?.accountId;
      if (updates?.categoryId !== undefined) updateData.category_id = updates?.categoryId;
      if (updates?.date !== undefined) updateData.date = updates?.date;
      if (updates?.description !== undefined) updateData.description = updates?.description;
      if (updates?.amount !== undefined) updateData.amount = updates?.amount;
      if (updates?.type !== undefined) updateData.type = updates?.type;
      if (updates?.cleared !== undefined) updateData.cleared = updates?.cleared;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;

      const { data, error } = await supabase?.from('transactions')?.update(updateData)?.eq('id', transactionId)?.select(`
          id,
          user_id,
          account_id,
          category_id,
          date,
          description,
          amount,
          type,
          cleared,
          notes,
          created_at,
          updated_at,
          accounts!inner(id, name, account_group),
          category:categories(id, name, color, icon)
        `)?.maybeSingle();
      
      if (error) throw error;
      if (!data) throw new Error('Transaction not found or you do not have permission to update it');
      
      return {
        id: data?.id,
        userId: data?.user_id,
        accountId: data?.account_id,
        account: data?.accounts?.name || '',
        accountGroup: data?.accounts?.account_group || '',
        categoryId: data?.category_id,
        category: data?.category?.name || '',
        categoryColor: data?.category?.color || '#6366f1',
        categoryIcon: data?.category?.icon || 'Tag',
        date: data?.date,
        description: data?.description,
        amount: parseFloat(data?.amount),
        type: data?.type,
        cleared: data?.cleared,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating transaction:', error);
      throw error;
    }
  },

  async delete(transactionId) {
    try {
      const { error } = await supabase?.from('transactions')?.delete()?.eq('id', transactionId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting transaction:', error);
      throw error;
    }
  },

  async bulkDelete(transactionIds) {
    try {
      const { error } = await supabase?.from('transactions')?.delete()?.in('id', transactionIds);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error bulk deleting transactions:', error);
      throw error;
    }
  },

  async bulkUpdateCategory(transactionIds, categoryId) {
    try {
      const { error } = await supabase?.from('transactions')?.update({ category_id: categoryId })?.in('id', transactionIds);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error bulk updating category:', error);
      throw error;
    }
  }
};